/**
 * Search Suggest Listener Module
 * Registers event listeners for search suggestion interactions
 */

import { getEventScope } from "../../core/event-scope/event-scope";
import type { SearchSuggestHandler, SearchSuggestOptions } from "./search-suggest-handler";
import { searchSuggestForm } from "./search-suggest-form";

let searchSuggestInstanceCounter = 0;

function getOrCreateSuggestInstanceId(inputElement: HTMLElement): string {
    const existing = inputElement.dataset.searchSuggestInstanceId;
    if (existing) return existing;
    const next = String(++searchSuggestInstanceCounter);
    inputElement.dataset.searchSuggestInstanceId = next;
    return next;
}

class SearchSuggestListener {
    public static bindSuggestSelect(inputElement: HTMLElement, onSelect: (event: Event) => void): void {
        inputElement.addEventListener("suggest:select", onSelect);
    }

    /** Fires once if a rendered suggestion's image fails to load (404/broken upload/...). */
    public static bindImageFallback(img: HTMLImageElement, onError: () => void): void {
        img.addEventListener("error", onError, { once: true });
    }

    public static setup(
        inputElement: HTMLElement,
        dropdown: HTMLElement,
        handler: SearchSuggestHandler,
        options: SearchSuggestOptions = {},
    ): void {
        if (inputElement.dataset.searchSuggestBound === "1") return;
        inputElement.dataset.searchSuggestBound = "1";

        inputElement.addEventListener("input", (e) => {
            handler.handleInput(e, dropdown, options);
        });

        inputElement.addEventListener("focus", async () => {
            const query = inputElement instanceof HTMLInputElement ? inputElement.value.trim() : "";

            if (query.length >= (options.minChars || 2)) {
                await handler.performSearch(query, dropdown, options);
            } else if (dropdown.children.length > 0) {
                handler.render.showDropdown(dropdown);
            }
        });

        inputElement.addEventListener("keydown", (e) => {
            handler.handleKeydown(e, inputElement, dropdown);
        });

        const instanceId = getOrCreateSuggestInstanceId(inputElement);
        const docScope = getEventScope(document, "search-suggest");
        docScope.once(`outside-click:${instanceId}`, () => {
            docScope.on("click", (e) => {
                const target = e.target;
                if (!(target instanceof Node)) return;
                if (inputElement.contains(target) || dropdown.contains(target)) return;
                handler.render.hideDropdown(dropdown);
                handler.activeIndex = -1;
            });
        });

        dropdown.addEventListener("click", (e) => {
            handler.handleItemClick(e, inputElement, dropdown, options);
        });

        dropdown.addEventListener("mouseover", (e) => {
            if (!(e.target instanceof Element)) return;
            const item = searchSuggestForm.getClosestItem(e.target);
            if (item instanceof HTMLElement) {
                const index = Number.parseInt(item.dataset.index || "", 10);
                handler.activeIndex = Number.isFinite(index) ? index : -1;
                handler.render.setActiveItem(dropdown, handler.activeIndex);
            }
        });
    }
}

export { SearchSuggestListener };
